import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  TrendingUp,
  TrendingDown,
  Warning,
  Schedule,
  CreditCard,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../../services/firebase';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { VenueAdminData } from '../../types/VenueAdminTypes';

const stripePromise = loadStripe(process.env.REACT_APP_JAMAPP_STRIPE_PUBLISHABLE_KEY || '');
const isDev = process.env.REACT_APP_JAMAPP_STRIPE_PUBLISHABLE_KEY?.startsWith('pk_test_');

interface SubscriptionPlan {
  planId: string;
  name: string;
  description: string;
  priceMonthly: number;
  priceUpfront: number;
  currency: string;
  limits: {
    maxVenues: number;
    totalOffersPerMonth: number;
    analyticsRetentionMonths: number;
    supportLevel: string;
  };
  features: {
    multiVenueManagement: boolean;
    advancedAnalytics: boolean;
    customBranding: boolean;
    apiAccess: boolean;
    prioritySupport: boolean;
  };
  isPopular: boolean;
  sortOrder: number;
  isActive: boolean;
}

interface SubscriptionManagementProps {
  open: boolean;
  venueAdminData: VenueAdminData;
  onClose: () => void;
  onSuccess: () => void;
}

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({ 
  open,
  venueAdminData, 
  onClose, 
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [showCardUpdate, setShowCardUpdate] = useState(false);
  const [billingType, setBillingType] = useState<'monthly' | 'upfront'>('monthly');

  useEffect(() => {
    if (open) {
      loadPlans();
      // Init billing type from current subscription
      const currentBilling = (venueAdminData?.subscription as any)?.billingType || 'monthly';
      setBillingType(currentBilling);
    }
  }, [open]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const plansQuery = query(
        collection(db, 'admin_subscription_plans'),
        where('isActive', '==', true),
        where('sortOrder', 'in', [1, 2]),
        orderBy('sortOrder', 'asc')
      );
      const plansSnapshot = await getDocs(plansQuery);
      const plansData = plansSnapshot.docs.map(doc => ({
        ...doc.data()
      } as SubscriptionPlan));

      setPlans(plansData);
    } catch (err: any) {
      console.error('Error loading plans:', err);
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const currentPlan = plans.find(p => p.planId === venueAdminData?.subscription?.planId);

  // Yearly lock-in: can only switch to monthly within 14 days of expiry
  const isOnYearlyPlan = (venueAdminData?.subscription as any)?.billingType === 'upfront';
  const currentPeriodEnd = (venueAdminData?.subscription as any)?.currentPeriodEnd;
  const daysUntilExpiry = currentPeriodEnd
    ? Math.ceil((currentPeriodEnd.toDate().getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : 999;
  const canSwitchToMonthly = !isOnYearlyPlan || daysUntilExpiry <= 14;
  const daysUntilCanSwitch = isOnYearlyPlan && !canSwitchToMonthly ? daysUntilExpiry - 14 : 0;

  const currentPlanOrder = currentPlan?.sortOrder ?? -1;
  const selectedPlanOrder = selectedPlan?.sortOrder ?? -1;

  const isUpgrade = selectedPlanOrder > currentPlanOrder;
  const isDowngrade = selectedPlanOrder < currentPlanOrder;
  const isSamePlan = selectedPlan?.planId === currentPlan?.planId;

  const canDowngrade = () => {
    if (!selectedPlan || !isDowngrade) return true;
    
    const wouldExceedVenueLimit = venueAdminData.venueCount > selectedPlan.limits.maxVenues;
    const wouldExceedOfferLimit = venueAdminData.subscription.totalOffersUsed > selectedPlan.limits.totalOffersPerMonth;
    
    return !wouldExceedVenueLimit && !wouldExceedOfferLimit;
  };

  const getDowngradeWarning = () => {
    if (!selectedPlan || !isDowngrade) return null;
    
    const warnings: string[] = [];
    if (venueAdminData.venueCount > selectedPlan.limits.maxVenues) {
      warnings.push(`You have ${venueAdminData.venueCount} venues but the ${selectedPlan.name} allows only ${selectedPlan.limits.maxVenues}`);
    }
    if (venueAdminData.subscription.totalOffersUsed > selectedPlan.limits.totalOffersPerMonth) {
      warnings.push(`You have ${venueAdminData.subscription.totalOffersUsed} active offers but the ${selectedPlan.name} allows only ${selectedPlan.limits.totalOffersPerMonth}`);
    }
    return warnings;
  };

  const handlePlanChange = async () => {
    if (!selectedPlan || isSamePlan) return;
    
    if (isDowngrade && !canDowngrade()) {
      setError('Cannot downgrade: Please reduce your venue or offer count first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const updateSubscription = httpsCallable(functions, 'updateVenueAdminSubscription');
      const result = await updateSubscription({
        newPlanId: selectedPlan.planId,
        billingType,
        changeType: isUpgrade ? 'upgrade' : 'downgrade'
      }) as any;

      if (result.data.success) {
        setSuccess(`Successfully scheduled ${isUpgrade ? 'upgrade' : 'downgrade'} to ${selectedPlan.name}!`);
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      }
    } catch (err: any) {
      console.error('Error changing plan:', err);
      setError(err.message || err.details || 'Failed to change subscription plan. Please try again or contact support.');
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  const handleCancelSubscription = async () => {
    setLoading(true);
    setError('');

    try {
      const cancelSubscription = httpsCallable(functions, 'cancelVenueAdminSubscription');
      const result = await cancelSubscription({
        reason: cancellationReason || 'No reason provided'
      }) as any;

      if (result.data.success) {
        setSuccess('Subscription will be canceled at the end of your billing period.');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 3000);
      }
    } catch (err: any) {
      console.error('Error canceling subscription:', err);
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setLoading(false);
      setShowCancelConfirmation(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(price / 100);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp.toDate()).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const ConfirmationDialog = () => (
    <Dialog 
      open={showConfirmation} 
      onClose={() => setShowConfirmation(false)} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          background: '#FFFFFF',
          backdropFilter: 'blur(16px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.125)',
          borderRadius: '16px',
        }
      }}
    >
      <DialogTitle>
        {isUpgrade ? (
          <Box display="flex" alignItems="center">
            <TrendingUp color="success" sx={{ mr: 1 }} />
            Confirm Upgrade
          </Box>
        ) : (
          <Box display="flex" alignItems="center">
            <TrendingDown color="warning" sx={{ mr: 1 }} />
            Confirm Downgrade
          </Box>
        )}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" paragraph>
          You are about to {isUpgrade ? 'upgrade' : 'downgrade'} from <strong>{currentPlan?.name}</strong> to <strong>{selectedPlan?.name}</strong>.
        </Typography>

        <Alert severity={isUpgrade ? "info" : "warning"} sx={{ mb: 2 }}>
          <Typography variant="body2">
            {isUpgrade ? (
              <>Your plan will upgrade immediately. You'll be charged the prorated difference at the end of your current billing period.</>
            ) : (
              <>Your plan will downgrade at the end of your current billing period ({formatDate(venueAdminData?.subscription?.renewalDate)}). You'll keep your current features until then.</>
            )}
          </Typography>
        </Alert>

        <Card 
          variant="outlined" 
          sx={{ 
            mt: 2,
            background: '#Ffffff',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Current Plan</Typography>
                <Typography variant="body1">{currentPlan?.name}</Typography>
                <Typography variant="h6" color="text.secondary">
                  {formatPrice(currentPlan?.priceMonthly || 0)}/mo
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">New Plan</Typography>
                <Typography variant="body1">{selectedPlan?.name}</Typography>
                <Typography variant="h6" color={isUpgrade ? 'success.main' : 'warning.main'}>
                  {formatPrice(selectedPlan?.priceMonthly || 0)}/mo
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>What changes:</Typography>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Max Venues:</Typography>
              <Typography variant="body2">
                {currentPlan?.limits.maxVenues} → {selectedPlan?.limits.maxVenues}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2">Monthly Offers:</Typography>
              <Typography variant="body2">
                {currentPlan?.limits.totalOffersPerMonth} → {selectedPlan?.limits.totalOffersPerMonth}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowConfirmation(false)} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePlanChange}
          disabled={loading}
          color={isUpgrade ? 'success' : 'warning'}
        >
          {loading ? <CircularProgress size={24} /> : `Confirm ${isUpgrade ? 'Upgrade' : 'Downgrade'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const CancellationDialog = () => (
    <Dialog 
      open={showCancelConfirmation} 
      onClose={() => setShowCancelConfirmation(false)} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          background: '#FFFFFF',
          backdropFilter: 'blur(16px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.125)',
          borderRadius: '16px',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Warning color="error" sx={{ mr: 1 }} />
          Cancel Subscription
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Your subscription will be canceled at the end of your current billing period ({formatDate(venueAdminData?.subscription?.renewalDate)}). 
            You'll continue to have access until then.
          </Typography>
        </Alert>

        <Typography variant="body2" paragraph>
          After cancellation:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 2 }}>
          <Typography component="li" variant="body2">Your venues will become inactive</Typography>
          <Typography component="li" variant="body2">All offers will be deactivated</Typography>
          <Typography component="li" variant="body2">You'll lose access to analytics</Typography>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Reason for cancellation (optional)"
          value={cancellationReason}
          onChange={(e) => setCancellationReason(e.target.value)}
          placeholder="Help us improve by telling us why you're canceling..."
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowCancelConfirmation(false)} disabled={loading}>
          Keep Subscription
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleCancelSubscription}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Cancel Subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading && plans.length === 0) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            background: '#FFFFFF',
            backdropFilter: 'blur(16px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.125)',
            borderRadius: '16px',
          }
        }}
      >
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            background: '#FFFFFF',
            backdropFilter: 'blur(16px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.125)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Manage Subscription</Typography>
            <Chip
              label={venueAdminData?.subscription?.status?.toUpperCase()}
              color={venueAdminData?.subscription?.status === 'active' ? 'success' : 'warning'}
              icon={venueAdminData?.subscription?.status === 'active' ? <CheckCircle /> : <Schedule />}
            />
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <Card sx={{ 
            mb: 3, 
            background: '#F5F5F5',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(79, 195, 247, 0.3)',
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Current Plan</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h4" color="primary">{currentPlan?.name}</Typography>
                  <Typography variant="h5" color="text.secondary" gutterBottom>
                    {formatPrice(currentPlan?.priceMonthly || 0)}/month
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Next billing: {formatDate(venueAdminData?.subscription?.renewalDate)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>Current Usage</Typography>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Venues:</Typography>
                    <Typography variant="body2">
                      {venueAdminData?.venueCount} / {currentPlan?.limits.maxVenues}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Monthly Offers:</Typography>
                    <Typography variant="body2">
                      {venueAdminData?.subscription?.totalOffersUsed} / {currentPlan?.limits.totalOffersPerMonth}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Billing type toggle */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Available Plans</Typography>
            <Box sx={{ display: 'flex', border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
              <Button
                size="small"
                onClick={() => setBillingType('monthly')}
                disabled={isOnYearlyPlan && !canSwitchToMonthly}
                sx={{
                  px: 2, py: 0.75, borderRadius: 0, border: 'none',
                  bgcolor: billingType === 'monthly' ? 'primary.main' : 'transparent',
                  color: billingType === 'monthly' ? 'white' : 'text.secondary',
                  '&:hover': { bgcolor: billingType === 'monthly' ? 'primary.dark' : '#f5f5f5' },
                  '&.Mui-disabled': { bgcolor: '#f5f5f5', color: 'text.disabled' },
                }}
              >
                Monthly
              </Button>
              <Button
                size="small"
                onClick={() => setBillingType('upfront')}
                sx={{
                  px: 2, py: 0.75, borderRadius: 0, border: 'none',
                  bgcolor: billingType === 'upfront' ? 'primary.main' : 'transparent',
                  color: billingType === 'upfront' ? 'white' : 'text.secondary',
                  '&:hover': { bgcolor: billingType === 'upfront' ? 'primary.dark' : '#f5f5f5' },
                }}
              >
                12 Months
                <Chip label="Save ~5%" size="small" sx={{ ml: 1, height: 18, fontSize: '0.6rem', bgcolor: '#e8f5e9', color: '#2e7d32' }} />
              </Button>
            </Box>
          </Box>

          {/* Yearly lock-in warning */}
          {isOnYearlyPlan && !canSwitchToMonthly && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You're on a 12-month plan. You can switch to monthly billing in <strong>{daysUntilCanSwitch} days</strong> (14 days before your renewal date).
            </Alert>
          )}

          <FormControl component="fieldset" fullWidth>
            <RadioGroup value={selectedPlan?.planId || currentPlan?.planId || ''}>
              <Grid container spacing={2}>
                {plans.map((plan) => {
                  const isCurrent = plan.planId === currentPlan?.planId &&
                    ((venueAdminData?.subscription as any)?.billingType || 'monthly') === billingType;
                  const isSelected = plan.planId === selectedPlan?.planId;
                  const displayPrice = billingType === 'upfront'
                    ? Math.round(plan.priceUpfront / 12)
                    : plan.priceMonthly;
                  const yearlySaving = plan.priceMonthly * 12 - plan.priceUpfront;

                  return (
                    <Grid size={{ xs: 12, md: 6 }} key={plan.planId}>
                      <Card
                        variant={isSelected ? "outlined" : "elevation"}
                        sx={{
                          top: 12,
                          border: isSelected ? 2 : 1,
                          borderColor: isSelected ? 'primary.main' : 'rgba(255, 255, 255, 0.125)',
                          cursor: 'pointer',
                          position: 'relative',
                          height: '100%',
                          background: isCurrent ? 'rgba(79, 195, 247, 0.1)' : '#F5F5F5',
                          backdropFilter: 'blur(8px)',
                          transition: 'all 0.3s ease',
                          '&:hover': { transform: 'translateY(-5px)', borderColor: 'primary.main' }
                        }}
                        onClick={() => setSelectedPlan(plan)}
                      >
                        {plan.isPopular && (
                          <Box sx={{
                            position: 'absolute', top: 12, left: '50%',
                            transform: 'translateX(-50%)',
                            bgcolor: 'secondary.main', color: 'white',
                            px: 2, py: 0.5, borderRadius: 1,
                            fontSize: '0.75rem', fontWeight: 'bold',
                          }}>
                            POPULAR
                          </Box>
                        )}
                        {isCurrent && (
                          <Chip label="CURRENT PLAN" color="primary" size="small"
                            sx={{ position: 'absolute', top: 12, right: 8 }} />
                        )}
                        <CardContent sx={{ pt: plan.isPopular ? 3 : 2 }}>
                          <FormControlLabel
                            value={plan.planId}
                            control={<Radio />}
                            label=""
                            sx={{ position: 'absolute', top: 8, left: 8, mb: 0 }}
                          />
                          <Typography variant="h6" gutterBottom sx={{ mt: 5 }}>
                            {plan.name}
                          </Typography>
                          <Typography variant="h4" color="primary" gutterBottom>
                            {formatPrice(displayPrice)}
                            <Typography component="span" variant="body2" color="text.secondary">
                              /month
                            </Typography>
                          </Typography>
                          {billingType === 'upfront' && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Billed as {formatPrice(plan.priceUpfront)} upfront
                              </Typography>
                              {yearlySaving > 0 && (
                                <Chip
                                  label={`Save ${formatPrice(yearlySaving)}/yr`}
                                  size="small"
                                  sx={{ ml: 1, bgcolor: '#e8f5e9', color: '#2e7d32', height: 20, fontSize: '0.65rem' }}
                                />
                              )}
                            </Box>
                          )}
                          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
                            {plan.description}
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Up to {plan.limits.maxVenues} venues</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{plan.limits.totalOffersPerMonth} monthly offers</strong>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {plan.limits.analyticsRetentionMonths} months analytics
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </RadioGroup>
          </FormControl>

          {isDowngrade && !canDowngrade() && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Cannot downgrade to this plan:
              </Typography>
              {getDowngradeWarning()?.map((warning, i) => (
                <Typography key={i} variant="body2">• {warning}</Typography>
              ))}
              <Typography variant="body2" sx={{ mt: 1 }}>
                Please delete some venues or wait for offers to expire before downgrading.
              </Typography>
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Box display="flex" gap={1}>
            <Button
              color="error"
              startIcon={<Cancel />}
              onClick={() => setShowCancelConfirmation(true)}
              disabled={loading || venueAdminData?.subscription?.status === 'canceled'}
            >
              Cancel Subscription
            </Button>
            <Button
              variant="outlined"
              startIcon={<CreditCard />}
              onClick={() => setShowCardUpdate(true)}
              disabled={loading}
            >
              Update Card
            </Button>
          </Box>
          <Box>
            <Button onClick={onClose} sx={{ mr: 1 }} disabled={loading}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => setShowConfirmation(true)}
              disabled={loading || isSamePlan || (isDowngrade && !canDowngrade())}
              startIcon={isUpgrade ? <TrendingUp /> : isDowngrade ? <TrendingDown /> : null}
              color={isUpgrade ? 'success' : isDowngrade ? 'warning' : 'primary'}
            >
              {isSamePlan ? 'Current Plan' : isUpgrade ? 'Upgrade Plan' : 'Downgrade Plan'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog />
      <CancellationDialog />
      {showCardUpdate && (
        <CardUpdateDialog
          open={showCardUpdate}
          stripeCustomerId={venueAdminData?.subscription?.stripeCustomerId}
          onClose={() => setShowCardUpdate(false)}
          onSuccess={() => {
            setShowCardUpdate(false);
            setSuccess('Payment method updated successfully');
          }}
        />
      )}
    </>
  );
};

// ── Card Update Dialog (needs its own Stripe Elements context) ────────────

interface CardUpdateDialogProps {
  open: boolean;
  stripeCustomerId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const CardUpdateForm: React.FC<CardUpdateDialogProps> = ({ open, stripeCustomerId, onClose, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const stripeInputSx = {
    p: '14px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    transition: 'border-color 0.2s',
    '&:focus-within': {
      borderColor: '#F24F2F',
      boxShadow: '0 0 0 2px rgba(242, 79, 47, 0.12)',
    },
  };

  const stripeElementStyle = {
    base: {
      fontSize: '16px',
      color: '#000000',
      fontFamily: "'ClashGrotesk', -apple-system, BlinkMacSystemFont, sans-serif",
      '::placeholder': { color: '#9e9e9e' },
    },
    invalid: { color: '#d32f2f', iconColor: '#d32f2f' },
    complete: { color: '#2e7d32', iconColor: '#2e7d32' },
  };

  const handleSave = async () => {
    if (!stripe || !elements) return;
    setSaving(true);
    setError('');

    try {
      // Create new payment method from card inputs
      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) throw new Error('Card element not found');

      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumber,
      });

      if (pmError) throw new Error(pmError.message);
      if (!paymentMethod) throw new Error('Failed to create payment method');

      // Send to Cloud Function to attach to Stripe customer + update subscription default
      const updateCard = httpsCallable(functions, 'updateVenueAdminPaymentMethod');
      await updateCard({ paymentMethodId: paymentMethod.id });

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to update payment method');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CreditCard color="primary" />
          Update Payment Method
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 1 }}>
          Enter your new card details. Your existing subscription will be updated immediately.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="success.main">✓ Payments Secured by Stripe</Typography>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
              Card Number
            </Typography>
            <Box sx={stripeInputSx}>
              <CardNumberElement options={{ style: stripeElementStyle }} />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                Expiry Date
              </Typography>
              <Box sx={stripeInputSx}>
                <CardExpiryElement options={{ style: stripeElementStyle }} />
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                CVC
              </Typography>
              <Box sx={stripeInputSx}>
                <CardCvcElement options={{ style: stripeElementStyle }} />
              </Box>
            </Box>
          </Box>

          {isDev && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Typography variant="caption">
                🧪 Test mode — use card: <strong>4242 4242 4242 4242</strong> · any future expiry · any CVC
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !stripe || !elements}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CreditCard />}
        >
          {saving ? 'Updating...' : 'Update Card'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Wrap in Elements so Stripe hooks work
const CardUpdateDialog: React.FC<CardUpdateDialogProps> = (props) => (
  <Elements stripe={stripePromise}>
    <CardUpdateForm {...props} />
  </Elements>
);

export default SubscriptionManagement;